/* Advisory only. Checkout, rates, discounts and app fee behavior remain Shopify-owned. */
(() => {
  function eligibleAmount(cart, mode, packagingId) {
    if (cart.currency !== 'USD' || !['total', 'exclude'].includes(mode)) return null;
    if (!Number.isFinite(cart.total_price)) return null;
    if (mode === 'total') return Math.max(0, cart.total_price);
    if (!packagingId || cart.cart_level_discount_applications?.length) return null;
    const packaging = cart.items.filter(item => String(item.product_id) === String(packagingId));
    if (packaging.some(item => !Number.isFinite(item.final_line_price))) return null;
    return Math.max(0, cart.total_price - packaging.reduce((sum, item) => sum + item.final_line_price, 0));
  }
  function calculate(cart, config) {
    const minimumAmount = eligibleAmount(cart, config.minMode, config.packagingId);
    const shippingAmount = eligibleAmount(cart, config.freeMode, config.packagingId);
    return {
      minimumRemaining: minimumAmount === null ? null : Math.max(0, 3000 - minimumAmount),
      shippingRemaining: shippingAmount === null ? null : Math.max(0, 16900 - shippingAmount),
      progress: shippingAmount === null ? null : Math.min(100, Math.max(0, shippingAmount / 16900 * 100))
    };
  }
  // Small pure function also exercised by fixtures; no cart mutation API is used.
  globalThis.SfsCartGuidanceMath = { eligibleAmount, calculate };
  if (!globalThis.customElements || customElements.get('sfs-cart-guidance')) return;
  customElements.define('sfs-cart-guidance', class extends HTMLElement {
    connectedCallback() {
      this.target = this.querySelector('[data-guidance]');
      this.config = this.dataset;
      this.active = ['total', 'exclude'].includes(this.config.minMode) || ['total', 'exclude'].includes(this.config.freeMode);
      if (!this.active) return;
      try { this.render(JSON.parse(this.querySelector('[data-cart]').textContent)); } catch { this.fallback(); }
      this.events = new AbortController();
      // Dawn pub/sub supplies native cart changes. Third-party apps may require a refresh.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
      window.addEventListener('pageshow', () => this.refresh(), { signal: this.events.signal });
    }
    disconnectedCallback() { this.unsubscribe?.(); this.events?.abort(); this.request?.abort(); }
    fallback() {
      this.target.replaceChildren();
      const p = document.createElement('p');
      p.textContent = '$30 livestock minimum. Free overnight shipping at $169+ for eligible destinations.';
      this.target.append(p);
    }
    async refresh() {
      this.request?.abort();
      this.request = new AbortController();
      // Never leave a stale "threshold reached" assertion while the cart is updating.
      this.fallback();
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { signal: this.request.signal, headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) throw new Error('Cart unavailable');
        const cart = await response.json();
        if (this.isConnected) this.render(cart);
      } catch (error) { if (error.name !== 'AbortError' && this.isConnected) this.fallback(); }
    }
    render(cart) {
      const state = calculate(cart, this.config);
      const money = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
      this.target.replaceChildren();
      const add = text => { const p = document.createElement('p'); p.textContent = text; this.target.append(p); };
      if (state.minimumRemaining === null) add('$30 livestock minimum.');
      else if (state.minimumRemaining > 0) add(`$30 order minimum — add ${money(state.minimumRemaining)} more.`);
      else add('$30 order threshold reached.');
      if (state.shippingRemaining === null) add('Free overnight shipping at $169+ for eligible destinations.');
      else {
        add(state.shippingRemaining > 0 ? `${money(state.shippingRemaining)} away from the $169 free overnight shipping threshold.` : 'Free overnight shipping threshold reached for eligible destinations.');
        const progress = document.createElement('progress');
        progress.max = 100; progress.value = state.progress;
        progress.setAttribute('aria-label', 'Progress toward the free overnight shipping threshold');
        this.target.append(progress);
      }
    }
  });
})();
